import Cookies from 'js-cookie';

interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  status?: string;
  error?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export async function apiRequest<T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  const { method = 'GET', headers = {}, body } = options;

  // Get auth token from cookie
  const token = Cookies.get('crm_auth_token');

  // Build headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Build request config
  const requestConfig: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include',
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    requestConfig.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  try {
    // Make the request
    // Strip leading /api if present since API_BASE_URL already includes it
    const cleanEndpoint = endpoint.replace(/^\/api/, '');
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${cleanEndpoint}`;
    const response = await fetch(url, requestConfig);

    // Parse response
    const data = await response.json();

    // Return formatted response
    return {
      success: data.success ?? response.ok,
      data: data.data ?? data,
      message: data.message,
      status: data.status,
      error: data.error
    };
  } catch (error: any) {
    console.error('API Request Error:', error);
    return {
      success: false,
      message: error.message || 'An error occurred',
      error: error.message
    };
  }
}
